import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query'

import type { ApiClient } from '@/api/client'
import { KPI_SPARKLINE_SOURCE_NAMES, type KpiSparklineSource } from '@/api/types'
import { useApiClient } from '@/api/ApiClientProvider'
import type { FormatOptions } from '@/design/format'
import { useCurrentTenant } from '@/lib/access'
import { drillDownHref, type DrillDown } from '@/lib/drilldown'
import type { GlobalFilters } from '@/lib/filters'
import { filtersKey, useGlobalFilters } from '@/lib/useGlobalFilters'

/**
 * One Overview card's data, keyed by the filters in the URL. While new filters
 * load, the previous figures stay on screen rather than collapsing to a
 * skeleton on every change.
 */
/** An Overview card's key root, `['overview', name]`: what another page marks stale when that card's source changes. */
export function overviewQueryKey(name: string) {
  return ['overview', name] as const
}

/** The Marketing performance card's TmT channel chart: the Overview's only read that carries `data_quality`. */
export const CHANNEL_CHART_QUERY = 'channel-chart'

export function useOverviewQuery<T>(
  name: string,
  fetch: (client: ApiClient, filters: GlobalFilters) => Promise<T>,
  extraKey: readonly unknown[] = [],
) {
  const client = useApiClient()
  const { filters } = useGlobalFilters()
  return useQuery({
    queryKey: [...overviewQueryKey(name), client.kind, filtersKey(filters), ...extraKey],
    queryFn: () => fetch(client, filters),
    placeholderData: keepPreviousData,
  })
}

export function useKpis() {
  return useOverviewQuery('kpis', (client, filters) => client.overviewKpis(filters))
}

/** `GET /analytics/signups` for the filters: the Sign-ups tile's sparkline, and the Customers page's Sign-ups panel. */
export function useSignups() {
  return useOverviewQuery('signups', (client, filters) => client.signups(filters))
}

/** How each sparkline source's daily points are read, one entry per source. */
const SPARKLINE_READS: Record<KpiSparklineSource, (client: ApiClient, filters: GlobalFilters) => Promise<number[]>> = {
  signups: async (client, filters) => (await client.signups(filters)).data.points.map((point) => point.signups),
}

/**
 * The daily points of every route a tile's sparkline can be drawn from
 * (`KPI_SPARKLINE_SOURCE_NAMES`, read by `SPARKLINE_READS`), one query each, beside the
 * KPIs so the grid never waits on them; a source not read yet, or that could not be
 * read, has none.
 */
export function useKpiSparklineSources(): Record<KpiSparklineSource, readonly number[]> {
  const client = useApiClient()
  const { filters } = useGlobalFilters()
  const reads = useQueries({
    queries: KPI_SPARKLINE_SOURCE_NAMES.map((source) => ({
      queryKey: ['overview', 'sparkline', source, client.kind, filtersKey(filters)],
      queryFn: () => SPARKLINE_READS[source](client, filters),
      placeholderData: keepPreviousData,
    })),
  })
  const points = (index: number): readonly number[] => reads[index]?.data ?? []
  return Object.fromEntries(KPI_SPARKLINE_SOURCE_NAMES.map((source, index) => [source, points(index)])) as Record<
    KpiSparklineSource,
    readonly number[]
  >
}

/** Builds a drill-down link that carries the current filters. */
export function useDrillDown() {
  const { filters } = useGlobalFilters()
  return (target: DrillDown) => drillDownHref(target, filters)
}

/**
 * The tenant's locale and currency, for every money figure: the answer's own
 * currency when it names one (`meta.currency`, which its amounts are in),
 * else the tenant's.
 */
export function useMoney(currency?: string): FormatOptions {
  const tenant = useCurrentTenant()
  return { currency: currency ?? tenant.data?.currency ?? 'GBP', locale: tenant.data?.locale ?? 'en-GB' }
}
